import React, { useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Card, Typography, Space, Row, Col } from 'antd';

import useClientTemplateClaimStatus from '../../models/useClientTemplateClaimStatus';
import useFirstClientForUser from '../../models/useFirstClientForUser';
import { StrikeColors } from '../../utils/Colors';
import ProductSwitcher from '../../utils/ProductSwitcher';
import { colorForBackgroundColor, getContrastWithRGBColor, rgbColorToHex } from '../../utils/RGBColorToHex';
import TrackerService from '../../utils/TrackerService';

import ContrastColor from './ContrastColor';
import QRImageView from './QRImageView';
import PoweredByStrike from './PoweredByStrike';

import 'antd/dist/antd.css';
import '../../App.scss';

const { Paragraph, Text, Title } = Typography;


const baseStyle = {
  width: '100%',
  minWidth: '260px',
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'center',
  padding: 0,
};

const contentContainerStyle = {
  maxWidth: '520px',
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'start',
  padding: 0,
  boxSizing: 'border-box',
};

const mainSizeStyle = { 
  width: '100%', 
  marginLeft: 'auto', marginRight: 'auto', 
  paddingLeft: '0px', paddingRight: '0px',
  boxSizing: 'border-box',
};

const bodySizeStyle = {
  width: '100%', 
  marginLeft: 'auto', marginRight: 'auto', 
  textAlign: 'start',
  padding: '0 16px',
  boxSizing: 'border-box',
}

const style_elImage = {
  height: 'auto',
  cursor: 'pointer',
  pointerEvents: 'auto',
  marginTop: '0px',
  marginBottom: '0px',
  maxWidth: '100%',
  maxHeight: '500px',
  padding: '0px',
  borderRadius: 0,
  paddingBottom: '16px',
  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',
  objectFit: 'contain'
};


function ActionButton({ text, intent, onClick, secondaryText, secondaryIntent, color, colour, textColor, ...rest }) {
  const buttonColor = color || StrikeColors.magenta;
  const contrastColor = textColor || colorForBackgroundColor(buttonColor);
  const { textLabel, linkLabel } = secondaryText || {};

  return (
    <>
      <Space size='small' direction='vertical' style={{ width: '100%' }}>

        <Button
          type='primary'
          onClick={() => (onClick({ intent }))}
          style={{ 
            fontSize: '26px', 
            height: '60px', 
            width: '100%',
            background: buttonColor, 
            borderColor: buttonColor, 
            borderRadius: '4px', 
            marginTop: 0,
            color: contrastColor,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <>{text}{' '}<ArrowRightOutlined style={{ fontSize: '16px', fontWeight: 'lighter', color: 'inherit', verticalAlign: 'middle' }} /></>
        </Button>

        {
          textLabel && 
          <p>{textLabel}</p>
        }
        <p></p>
        {
          secondaryText && secondaryIntent &&
          <Button
            type='default'
            onClick={() => (onClick({ intent: `${secondaryIntent}` }))}
            style={{
              width: '100%',
              height: '60px',
              fontSize: '20px',
            }}
          >
            {linkLabel || secondaryText}
          </Button>
        }
      </Space>
    </>
  );
}


function trackTagData({ event, tagId, tag, user }) {
  const isTest = !!(tag.is_test);

  const tracker = new TrackerService({
    tagId,
    isTest,
    object: tag,
  });

  let dataToTrack = {
    tagId: tagId,
    isAnonymous: user.isAnonymous,
  };

  if (tag && tag.templateTagId) {
    dataToTrack = {
      ...dataToTrack,
      templateTagId: tag.templateTagId,
    };
  }

  tracker.mixpanelTrack(event, {
    distinct_id: user.uid,
    ...dataToTrack
  });

  if (!isTest) {
    tracker.googleTrack(
      event,
      {
        userId: user.uid,
        ...dataToTrack
      }
    );
  }

}

export function ClaimTagComponent({ tagId, tag, user, locStrings, loading, onAction, ...rest }) {
  if (!tag) return null;

  const {
    product,
    avatar,
    actionText,
    displayName,
    profileBio,
    is_test,
    rgbColor,
    fgColor,
  } = tag;

  const isAnonymous = user && user.displayName ? false : true;
  const client = useFirstClientForUser(user);
  const claimStatus = useClientTemplateClaimStatus(client, tag);
  const { status, error: claimStatusError, templateTagId: templateName } = claimStatus || {};
  const claimLoading = claimStatus.status === 'loading';
  const claimAllowed = claimStatus.status === 'allowed';

  const topBackgoundColor = rgbColor || StrikeColors.magenta;
  const baseContrastColor = colorForBackgroundColor(topBackgoundColor);
  const contrastColor = (fgColor && fgColor !== 'null') ? fgColor : baseContrastColor;
  
  const topSectionStyle = { 
    backgroundColor: topBackgoundColor, 
    width: '100%',
    padding: 'calc(48px + env(safe-area-inset-top, 0px)) 0 48px 0',
          marginBottom: '20px',
    marginTop: 0,
  };

  const isTest = !!is_test;
  const shouldShowScanMeText = window.innerWidth >= 768;

  const onClickButton = ({ intent }) => {
    const event = `start_${intent}_clicked`;
    trackTagData({ event, tagId, tag, user });
    if (onAction) onAction(intent, { fromClick: true });
  };


  const productSwitcher = new ProductSwitcher({ locStrings });
  const productMap = productSwitcher.getProductMap(product);
  let { 
    heading, 
    subheading, 
    subsubheading, 
    confirmClaimButtonText, 
    defaultSignupText, 
    signupWarningText 
  } = productMap;

  confirmClaimButtonText = locStrings.begin_claim_start_button || confirmClaimButtonText;
  defaultSignupText = locStrings.begin_claim_signup_button || defaultSignupText;
  signupWarningText = locStrings.begin_claim_tag_part_1 || signupWarningText;

  let additionalInfoForTag = [];
  let signupImage = null;

  if (actionText) {
    heading = actionText.heading || heading;
    subheading = actionText.subheading || subheading;
    subsubheading = actionText.subsubheading || subsubheading;
    confirmClaimButtonText = actionText.claim || confirmClaimButtonText;
    defaultSignupText = actionText.signup || defaultSignupText;
    additionalInfoForTag = actionText.questions || additionalInfoForTag;
    signupWarningText = actionText.questionsTitle || signupWarningText;
    signupImage = actionText.signupImage || signupImage;
  }

  let supportingTextForButton = isAnonymous ?
    locStrings.begin_claim_tag_part_2_new :
    locStrings.begin_claim_tag_part_2_existing;

  return (
    <div style={baseStyle}>
      <div style={topSectionStyle}>
        <div style={contentContainerStyle}>
          <div style={bodySizeStyle}>
            <Title level={1} style={{ color: contrastColor, margin: 0, fontSize: '36px', fontWeight: 'bold' }}>{heading}</Title>
            <Text style={{ color: contrastColor, opacity: 0.9, fontSize: '20px', display: 'block', marginBottom: '24px' }}>{subheading}</Text>
          </div>
          
          <div style={{ textAlign: 'center', paddingBottom: '12px', width: '100%' }}>
            {(signupImage || avatar) 
              ?
              <img style={style_elImage} src={(signupImage || avatar)} alt="logo" />
              :
              <div style={bodySizeStyle}>
                <QRImageView tag={tag} tagId={tagId} nosubtext={true} locStrings={locStrings} />
                { shouldShowScanMeText &&
                  <p style={{ textAlign: 'center', fontSize: '16px', fontStyle: 'italic', color: contrastColor }}>
                    To continue on your phone, scan this QR with your phone camera
                  </p>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div style={contentContainerStyle}>
        <Space direction='vertical' size='large' style={mainSizeStyle}>
          <div style={bodySizeStyle}>
            <div style={{ textAlign: 'start'}}>
              <div>
                { !claimLoading && claimAllowed &&
                  <ActionButton
                    text={isAnonymous ? defaultSignupText : confirmClaimButtonText}
                    intent={isAnonymous ? 'signup' : 'claim'}
                    secondaryText={isAnonymous ? null : 'Go to Dashboard'}
                    secondaryIntent={isAnonymous ? 'signin' : 'dashboard'}
                    onClick={onClickButton}
                    color={rgbColor || (isAnonymous ? StrikeColors.magenta : StrikeColors.blue)}
                    textColor={baseContrastColor}
                  />
                }
                { !claimLoading && !claimAllowed &&
                  <ActionButton
                    text={'Go to Dashboard'}
                    intent={'dashboard'}
                    secondaryText={null}
                    secondaryIntent={null}
                    onClick={onClickButton}
                    color={rgbColor || (isAnonymous ? StrikeColors.magenta : StrikeColors.blue)}
                    textColor={baseContrastColor}
                  />
                }
              </div>
              { claimStatus && !claimAllowed &&
                <Paragraph style={{ marginTop: '16px' }}>
                  {`You already have ${claimStatus.count || 0} of these activated. Proceed to dashboard.`}
                </Paragraph>
              }
              {
                isAnonymous &&
                <Title level={5} style={{ marginTop: '16px' }}>{subsubheading}</Title>
              }
              { !isAnonymous && user && user.email && user.displayName &&
                <Paragraph style={{ marginTop: '16px' }}>{`${supportingTextForButton} ${user.email} (${user.displayName})`}</Paragraph>
              }
            </div>
          </div>

          <div style={bodySizeStyle}>
            {
              additionalInfoForTag.length > 0 &&
              <Text strong style={{ display: 'block', marginTop: '24px', marginBottom: '12px', fontSize: '18px' }}>
                {signupWarningText}
              </Text>
            }
            {
              additionalInfoForTag.map((info, index) => {
                const q = typeof info === 'string' ? info : info?.question;
                const a = typeof info === 'string' ? null : info?.answer;
                return (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '16px', display: 'block' }}>{q}</Text>
                    {a && <Text style={{ fontSize: '14px', display: 'block', color: 'rgba(0,0,0,0.65)', marginTop: '4px' }}>{a}</Text>}
                  </div>
                )
              })
            }
          </div>
        </Space>
      </div>
    </div>
  );

}


